import { castDate, dbDate } from "@caredna-marlin/utils/dates";
import {
  addDays,
  addHours,
  differenceInMinutes,
  isToday,
  startOfDay,
  startOfHour,
  startOfToday,
} from "date-fns";
import { minBy } from "lodash";
import { useEffect, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { CustomMbscCalendarEventData } from "~/utils/eventsHelper";

export type CommandCenterLocationState = {
  date: Date,
  visitResourceId?: number,
} | undefined;

export const useDateSelector = () => {
  // Other parts of the app send the user to the command center with a date in location.state; if we have one, use that for the initial
  // day to display.
  const locationState = useLocation().state as CommandCenterLocationState;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // A date query param takes precedence over location state. It must be applied synchronously on
  // the first render: rendering on "today" first and navigating to the target date in an effect
  // makes the date-scoped queries fetch two different day windows, and the router transition
  // between them can oscillate under load, refetching both windows in a loop.
  const dateParam = params.get("date");
  const date = dateParam
    ? castDate(dateParam)
    : locationState?.date ? new Date(locationState.date) : startOfToday();

  // Move the date param into location state and remove it from the URL, so that subsequent
  // day navigation (which only updates location state) takes effect.
  useEffect(() => {
    if (dateParam) {
      setDate(date);
    }
  }, []);

  const setDate = (date: Date) => {
    navigate(".", { replace: true, state: { date } as CommandCenterLocationState });
  }
  const dateStr = dbDate(date);
  const changeDays = (num: number) => setDate(startOfDay(addDays(date, num)));
  const previousDay = () => changeDays(-1);
  const nextDay = () => changeDays(1);
  const today = () => setDate(new Date());
  const reset = () => setDate(startOfToday());
  return { date, dateStr, previousDay, nextDay, today, reset, setDate };
};

export const useCalendarDateTimeConverter = (
  date: Date,
  events: CustomMbscCalendarEventData[]
) => {
  // This time is memoized so that it only changes when `data` changes, and not when `events`
  // do - avoiding mobiscroll constantly scrolling the command center every time an event changes.
  // BEWARE!!: Removing this useMemo does causes a render loop. We've added changes for not needing useMemo here https://github.com/medarrive/caredna-marlin/pull/840
  const twoHoursAgo = useMemo(() => addHours(startOfHour(new Date()), -2), [date]);

  return useMemo(() => {
    if (startOfDay(date).getTime() !== date.getTime()) {
      // If we have a time other than the start of the day, then we've been deep-linked to a specific date and
      // time. Respect it.
      return date;
    }

    const selectedDbDate = dbDate(date);
    const dayEvents = events.filter((event) => event.dbDate === selectedDbDate)

    const earliestShift = minBy(dayEvents, (shift) => shift.startDate);
    // If we don't have any shift events, then arbitrarily scroll to 7am as the starting point.
    const startOfWorkday = earliestShift?.startDate || addHours(startOfDay(date), 7);

    // If showing today, and it's late enough in the day, scroll towards current time
    // Otherwise, scroll to beginning of the day
    if (
      isToday(date) &&
      differenceInMinutes(new Date(), startOfWorkday) > 180
    ) {
      return twoHoursAgo;
    } else {
      return startOfWorkday;
    }
  }, [date, events]);
};
